/* ──────────────────────────────────────────────────────────────────────
   Map machine review-reason codes to plain English explanations.
   Codes live in crawler.EditionMaster_Crawler.ReviewReason as a
   comma-separated list (see backfill_catalog_review.py).

   Reviewers should never see "added_from_dtl_gap" — they see the human
   text plus a suggested action.
   ────────────────────────────────────────────────────────────────────── */

export interface ReviewReasonInfo {
  code:    string;     // canonical machine key
  title:   string;     // short heading, sentence-case
  body:    string;     // 1-2 sentences explaining why the row was flagged
  action:  string;     // what the reviewer should do next
}

const TABLE: Record<string, ReviewReasonInfo> = {
  added_from_dtl_gap: {
    code:  "added_from_dtl_gap",
    title: "Auto-inserted from DashboardTrackLive",
    body:  "This channel had activity in DashboardTrackLive but was missing from the catalog. We inserted a placeholder edition row with the channel's metadata so coverage stays complete.",
    action: "Fill in the correct City / State / Edition name. Once corrected, clear the review flag.",
  },
  duplicate_channel_name: {
    code:  "duplicate_channel_name",
    title: "Duplicate channel name",
    body:  "Another channel exists in dbo.Channel with exactly the same name. Likely a data-entry duplicate or two distinct outlets that need disambiguation.",
    action: "Investigate which ChannelId is canonical. Merge if duplicate, or rename one to clarify (e.g. add the city to the name).",
  },
  no_dtl_activity: {
    code:  "no_dtl_activity",
    title: "No DashboardTrackLive activity",
    body:  "This channel exists in the catalog but has zero rows in dbo.DashboardTrackLive across all history. It may be defunct, recently added, or a ChannelId that was renumbered.",
    action: "Confirm whether the publication is still operational. If not, mark IsActive=0 (soft-delete). If still active, document why we have no historic activity.",
  },
  channel_dtl_cat_mismatch: {
    code:  "channel_dtl_cat_mismatch",
    title: "Category disagreement with DashboardTrackLive",
    body:  "dbo.Channel.Category disagrees with the dominant category recorded in dbo.DashboardTrackLive (with at least 5 corroborating rows). One of them is wrong.",
    action: "Look at recent issues for this channel. Update the Category on the Channel tab if needed, then clear the review flag.",
  },
  null_category: {
    code:  "null_category",
    title: "Missing category",
    body:  "Category is NULL on dbo.Channel for this publisher — every active channel should be categorised.",
    action: "Set the Category on the Channel tab (Regional / Mainline / Financial / Periodical).",
  },
  null_language: {
    code:  "null_language",
    title: "Missing language",
    body:  "Language is NULL on dbo.Channel for this publisher.",
    action: "Set the Language on the Channel tab.",
  },
  bad_language_chars: {
    code:  "bad_language_chars",
    title: "Cleaned bad language characters",
    body:  "The Language value contained embedded newline characters (e.g. 'Tamil\\n'). We've cleaned it; this is here for the audit log.",
    action: "Verify the cleaned value looks right and clear the flag.",
  },
};

export const REVIEW_REASONS = TABLE;

/** Parse the comma-separated `ReviewReason` field into typed info entries. */
export function parseReviewReasons(raw: string | null | undefined): ReviewReasonInfo[] {
  if (!raw) return [];
  return raw.split(",")
    .map(s => s.trim())
    .filter(Boolean)
    .map(code => TABLE[code] ?? ({
      code, title: code, body: "(no description for this code)", action: "",
    }));
}
